import { Col, Container, Row, Space, SpanConfig, useMediaQuery } from '@ag1/ui';
import { urlForFile } from '@portal/shared/utils/urlFor';
import clsx from 'clsx';
import { LottieRefCurrentProps } from 'lottie-react';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import { ImageAndTextV3 as ImageAndTextV3Props, SanityKeyed } from 'types/studio/studio';
import { StudioBaseImageV2 } from '../StudioBaseImage/StudioBaseImage';
import { CommonSectionProps } from '../types';
import ImageAndTextContentV3 from './ImageAndTextContentV3/ImageAndTextContentV3';
import styles from './ImageAndTextV3.module.scss';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

const ImageAndTextV3 = (props: SanityKeyed<ImageAndTextV3Props> & CommonSectionProps) => {
  const {
    _key: key,
    _type: sectionType,
    subtitle,
    headline,
    headlineSize,
    headlinePosition,
    secondSubtitle,
    text,
    buttonCTA,
    baseImage,
    contentPosition,
    imageSize,
    imageSquareOnTablet,
    imageFull,
    backgroundColor,
    theme,
    padding,
    useChart,
    chart,
    mobileImageOrder,
    imageAlignment,
    visibleOnPageLoad
  } = props;

  const imageColConfig = useMemo(
    () =>
      (imageSize === 's' ? { xxs: 12, m: 6 } : { xxs: 12, m: 6, l: 5, xl: 6 }) satisfies SpanConfig,
    [imageSize]
  );

  const infoColConfig: SpanConfig =
    imageSize === 's'
      ? { xxs: 12, m: 6, l: { value: 5, wide: true } }
      : { xxs: 12, m: 6, l: { value: 6, wide: true }, xl: { value: 5, wide: true } };

  const atSmall = useMediaQuery('s');
  const [isVisible, setIsVisible] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [animationData, setAnimationData] = useState<{
    mobile: any;
    desktop: any;
    loop: boolean;
  }>();
  const [lottieError, setLottieError] = useState(false);

  // Generate a unique identifier for this instance
  const instanceId = useRef(`imageAndTextV3_${key}`);

  useEffect(() => {
    if (isVisible && animationData && lottieRef.current) {
      lottieRef.current.play();
    }
  }, [isVisible, animationData]);

  useEffect(() => {
    if (!animationData && chart && chart.mobile && chart.desktop) {
      Promise.all([fetch(urlForFile(chart.mobile)), fetch(urlForFile(chart.desktop))])
        .then((responses) => Promise.all(responses.map((response) => response.json())))
        .then(([mobile, desktop]) => {
          setAnimationData({ mobile, desktop, loop: chart.loop ?? false });
        });
    }
  }, [chart, animationData]);

  useEffect(() => {
    if (animationData && wrapperRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && entries[0].intersectionRatio > 0.3) {
            setIsVisible(true);
            observer.unobserve(wrapperRef.current!);
          }
        },
        { threshold: [0.3] }
      );
      observer.observe(wrapperRef.current);
      return () => observer.disconnect();
    }
    return () => {};
  }, [animationData]);

  const lottieStyle = {
    height: 'auto',
    width: '100%',
    maxWidth: '600px',
    overflow: 'hidden',
    display: 'flex'
  };

  const renderLottie = () => (
    <Lottie
      key={`${instanceId.current}_lottie`}
      lottieRef={lottieRef}
      animationData={atSmall ? animationData?.desktop : animationData?.mobile}
      loop={animationData?.loop}
      autoplay
      className={styles.lottie}
      style={lottieStyle}
      rendererSettings={{ preserveAspectRatio: 'xMidYMid slice' }}
      onError={(error) => {
        // eslint-disable-next-line no-console
        console.error(`Lottie error for ${instanceId.current}:`, error);
        setLottieError(true);
      }}
    />
  );

  const renderImage = () => (
    <StudioBaseImageV2
      image={baseImage.image}
      mobileImage={baseImage.mobileImage}
      tabletImage={baseImage.tabletImage}
      sizes={imageColConfig}
      className={clsx(
        imageSize === 'l' && styles.imageContLarge,
        imageSize === 'xl' && styles.imageContXl,
        imageSize === 's' && styles.imageContSmall,
        imageSize === 'm' && styles.imageContMedium,
        imageSquareOnTablet && styles.imageSquare
      )}
      childClassName={[
        styles.imageLarge,
        contentPosition === 'left' && styles.imageLeft,
        imageSize === 'xl' && styles.right
      ]}
      eagerLoading={visibleOnPageLoad}
    />
  );

  const renderContent = () => (
    <ImageAndTextContentV3
      sectionType={sectionType}
      headline={headline}
      headlineSize={headlineSize}
      subtitle={subtitle}
      secondSubtitle={secondSubtitle}
      theme={theme}
      text={text}
      headlinePosition={headlinePosition}
      buttonCTA={buttonCTA}
      backgroundColor={backgroundColor}
    />
  );

  const atMedium = useMediaQuery('m');

  return (
    <div
      className={clsx(
        styles.root,
        contentPosition === 'left' && styles.reverse,
        backgroundColor === 'gray' && styles.grayBg,
        backgroundColor === 'green' && styles.greenBg,
        !imageFull && styles.desktop
      )}
      ref={wrapperRef}
      id={instanceId.current}>
      <Container narrow>
        <div className={clsx(styles.defaultPadding, getSectionPaddingClasses(styles, padding))}>
          <Row
            align={{ l: 'left' }}
            className={clsx(
              styles.root,
              contentPosition === 'left' && styles.reverse,
              imageAlignment === 'bottom' && styles.alignToBottom
            )}>
            <Col span={imageColConfig} className={clsx(styles.col)}>
              {useChart &&
              animationData &&
              !lottieError &&
              (atMedium || mobileImageOrder === 'before' || mobileImageOrder === undefined)
                ? renderLottie()
                : baseImage.image && !useChart && renderImage()}
            </Col>
            <Col span={infoColConfig} className={clsx(contentPosition !== 'left' && styles.clearM)}>
              <Space top={{ xxs: 24, s: 24, m: 0 }}>{renderContent()}</Space>
            </Col>
            {mobileImageOrder === 'after' && !atMedium && (
              <Col
                span={imageColConfig}
                className={clsx(styles.col, styles.mobileSection)}
                style={{ minHeight: '300px' }}>
                {useChart && animationData && !lottieError
                  ? renderLottie()
                  : baseImage.image && !useChart && renderImage()}
              </Col>
            )}
          </Row>
        </div>
      </Container>
    </div>
  );
};

export default ImageAndTextV3;
